import { ActionIcon, Alert, Button, Drawer, Menu } from '@mantine/core';
import {
  IconCalendarClock,
  IconClipboardPlus,
  IconDots,
  IconEye,
  IconListTree,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { ComponentDetails } from './ComponentDetails.js';
import { CounterDialog } from './CounterDialog.js';
import type {
  Measurement,
  MeasurementPoint,
  OutOfPmsReason,
  PmsAlertsView,
} from './condition-types.js';
import { utcToShipLocal } from './counter-form.js';
import { EquipmentDrawer, usePmsErrorText } from './EquipmentDrawer.js';
import { EquipmentOverview } from './EquipmentOverview.js';
import { EquipmentTree } from './EquipmentTree.js';
import { type EquipmentFormValues, toEquipmentInput } from './equipment-form.js';
import {
  addParentFor,
  ancestorIds,
  defaultExpanded,
  defaultSelection,
  equipmentOptions,
  overviewCounts,
  readPmsQuery,
  treeKey,
  treeRows,
  withOpen,
  writePmsQuery,
} from './equipment-tree.js';
import { OutOfPmsDialog } from './OutOfPmsDialog.js';
import classes from './Pms.module.css';
import {
  MaintenancePlanPanel,
  PmsCalendar,
  QuickLinks,
  UpcomingTasksPanel,
  WorkOrderPanel,
} from './PmsPanels.js';
import {
  calendarMarks,
  descendantSet,
  editedOnBoardOf,
  type OverviewRow,
  overviewRows,
  overviewSummary,
} from './plan-view.js';
import { RotablesPanel } from './RotablesPanel.js';
import type { PmsSource } from './source.js';
import {
  type EquipmentDetail,
  type HistoryEntry,
  type InstructionOption,
  isQueuedChange,
  type JobList,
  type PlanView,
  type Reading,
  type EquipmentTree as Tree,
  type VesselParticulars,
  type WorkOrderList,
} from './types.js';

/** Bakım verisi (M7b3): Overview, Component Details ve alt panellerin işleri ve kayıtları. */
interface WorkData {
  vesselId: string;
  plan: PlanView | null;
  jobs: JobList | null;
  workOrders: WorkOrderList | null;
  history: HistoryEntry[];
  instructions: InstructionOption[];
  /** Geminin ölçüm noktaları (M7c). */
  points: MeasurementPoint[];
  /** PMS uyarıları ve PMS dışı ekipman (K22, K25). */
  alerts: PmsAlertsView | null;
}

import { vesselTime } from '../personnel/ship-clock.js';
import { LibraryRequestsBanner } from '../pms-library/LibraryRequests.js';
import { LibrarySetupEmpty, libraryApplyPath } from '../pms-library/library-setup.js';
import type { LibraryRequestSource } from '../pms-library/source.js';
import { SetupChangesBanner } from './SetupChangesBanner.js';
import { setupChangesPath } from './types.js';
import { useReportExport } from './use-report-export.js';
import { VesselPmsCard } from './VesselPmsCard.js';

export type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; particulars: VesselParticulars; tree: Tree };

/**
 * Yeniden okurken ekranda kalan veri (M7A-SCR-10): kayıttan sonra aynı geminin ağacı yerinde
 * kalır (sayfa sıçramaz); gemi değişince eski geminin ağacı gösterilmez, yükleniyor görünür.
 */
export function reloadingData(current: Data, vesselId: string): Data {
  return current.kind === 'ready' && current.tree.vesselId === vesselId
    ? current
    : { kind: 'loading' };
}

/** Kaydedilen düğüm: yolu, kayıttan önceki ağaç değil yeniden okunan ağaç gelince açılır. */
export interface Reveal {
  id: string;
  staleTree: Tree | null;
}

/**
 * Açılacak yol (M7A-SCR-11): kayıttan önceki ağaçta beklenir (düzenlemede düğüm eski yerindedir),
 * yeniden okunan ağaçta düğüm görününce atalarının kimlikleri döner.
 */
export function revealPath(tree: Tree | null, reveal: Reveal | null): string[] | null {
  if (!tree || !reveal || tree === reveal.staleTree) return null;
  if (!tree.nodes.some((n) => n.id === reveal.id)) return null;
  return ancestorIds(tree.nodes, reveal.id);
}

type Detail =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; detail: EquipmentDetail; readings: Reading[] };

export interface EquipmentListScreenProps {
  source: PmsSource;
  /** Saat (test ve ekran görüntüsü); takvimin "bugün"ü ve okuma zamanı gemi saatiyle. */
  now?: () => Date;
  /** Gemide ofisin PMS kütüphanesi talepleri (ADR 0063 §7); bekleyen yoksa çizilmez. */
  libraryRequests?: LibraryRequestSource;
}

const systemNow = () => new Date();

/**
 * Maintenance (PMS) ▸ Equipment List (`gemi-pms-ekipman.jpeg`; plan M7 Task 6b): üst kart, ağaç,
 * Equipment Overview, Component Details; altta Maintenance Plan, Upcoming Tasks, Work Order, PMS
 * Calendar ve Quick Links. İki uygulamada aynı ekran (ADR 0019 §2): gemide etkin geminin ana
 * verisini gemi yazar; ofiste yalnız kurulum devrindeki gemi düzenlenir, etkin gemide "Edited on
 * board" görünür (K1). Sayaç okuması yalnız gemide (K13). Seçim adreste (`?equipment=`; web'de
 * ayrıca `?vessel=`).
 */
export function EquipmentListScreen({
  source,
  now = systemNow,
  libraryRequests,
}: EquipmentListScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const errorText = usePmsErrorText();
  const [localSearch, setLocalSearch] = useState('');
  const search = shell ? shell.search : localSearch;
  const query = readPmsQuery(search);
  const setQuery = useCallback(
    (patch: Parameters<typeof writePmsQuery>[1]) => {
      const next = writePmsQuery(search, patch);
      if (next === (search || '')) return;
      if (shell) shell.navigate(`${shell.path}${next}`);
      else setLocalSearch(next);
    },
    [search, shell],
  );

  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [vesselsError, setVesselsError] = useState<string | null>(null);
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [detail, setDetail] = useState<Detail>({ kind: 'idle' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** Arama sürerken kullanıcının kapattığı (aramanın açtığı) düğümler; arama değişince sıfırlanır. */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [treeQuery, setTreeQuery] = useState('');
  /** Ağaç yeniden okununca yolu açılacak düğüm (kayıttan sonra; M7A-SCR-11). */
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [drawer, setDrawer] = useState<'add' | 'edit' | null>(null);
  const [counterOpen, setCounterOpen] = useState(false);
  const [treeDrawer, setTreeDrawer] = useState(false);
  const [skeletonError, setSkeletonError] = useState<unknown>(null);
  /** "Critical only" süzgeci (K22) ve elle "Out of PMS" penceresi (K25). */
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [outOfPmsMode, setOutOfPmsMode] = useState<'mark' | 'unmark' | null>(null);
  const [reloads, setReloads] = useState(0);
  const [workData, setWorkData] = useState<WorkData | null>(null);
  /**
   * Kayıttan sonra seçilecek düğüm. Çekmece kapanıp kaydedilmemiş değişiklik kaydı silindikten sonra
   * adrese yazılır; yoksa kabuğun koruması kaydedilmiş formu "kirli" sanıp onay sorar.
   */
  const [pending, setPending] = useState<{ id: string | null; staleTree: Tree | null } | null>(
    null,
  );
  const initialised = useRef<string | null>(null);

  // Gemiler (web: filo; gemi: tek gemi)
  useEffect(() => {
    let live = true;
    source
      .vessels()
      .then((list) => {
        if (live) setVessels(list);
      })
      .catch((error: unknown) => {
        if (live) setVesselsError(errorCode(error));
      });
    return () => {
      live = false;
    };
  }, [source]);

  const vesselId = useMemo(() => {
    if (!vessels || vessels.length === 0) return null;
    if (source.scope === 'web' && query.vessel && vessels.some((v) => v.id === query.vessel)) {
      return query.vessel;
    }
    return vessels[0]?.id ?? null;
  }, [vessels, query.vessel, source.scope]);
  const exporter = useReportExport(source, vesselId);

  // Gemi değişince ağaç ve gemi özellikleri yeniden okunur; süzgeçler sıfırlanır
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!vesselId) return;
    let live = true;
    setData((current) => reloadingData(current, vesselId));
    Promise.all([source.particulars(vesselId), source.tree(vesselId)])
      .then(([particulars, tree]) => {
        if (live) setData({ kind: 'ready', particulars, tree });
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);

  // Bakım verisi (M7b3): plan, işler, iş emirleri, geçmiş ve talimatlar; okunamazsa paneller boş
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!vesselId || !source.plan) return;
    let live = true;
    const soft = <T,>(promise: Promise<T> | undefined, fallback: T) =>
      (promise ?? Promise.resolve(fallback)).catch(() => fallback);
    Promise.all([
      soft(source.plan(vesselId), null),
      soft(source.jobs?.(vesselId), null),
      soft(source.workOrders?.(vesselId), null),
      soft(source.history?.(vesselId), [] as HistoryEntry[]),
      soft(source.instructions?.(vesselId), [] as InstructionOption[]),
      soft(
        source.measurementPoints?.(vesselId).then((list) => list.items),
        [] as MeasurementPoint[],
      ),
      soft(source.alerts?.(vesselId), null as PmsAlertsView | null),
    ]).then(([plan, jobs, workOrders, history, instructions, points, alerts]) => {
      if (live) {
        setWorkData({ vesselId, plan, jobs, workOrders, history, instructions, points, alerts });
      }
    });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);

  const tree = data.kind === 'ready' ? data.tree : null;
  const nodes = tree?.nodes ?? [];
  const selectedId = tree ? defaultSelection(nodes, query.equipment) : null;

  // İlk yüklemede (ve gemi değişince) seçilenin yolu açılır
  useEffect(() => {
    if (!tree) return;
    const key = tree.vesselId;
    if (initialised.current === key) return;
    initialised.current = key;
    setExpanded(defaultExpanded(tree.nodes, defaultSelection(tree.nodes, query.equipment)));
    setCollapsed(new Set());
    setTreeQuery('');
    setFocusedId(null);
  }, [tree, query.equipment]);

  // Kaydedilen düğüm yeniden okunan ağaçta görününce yolu bir kez açılır (kapalı dalda kalmaz) ve
  // sekme durağı olur (M7A-SCR-11)
  useEffect(() => {
    const path = revealPath(tree, reveal);
    if (path === null || reveal === null) return;
    setExpanded((open) => new Set([...open, ...path]));
    setCollapsed((closed) => new Set([...closed].filter((id) => !path.includes(id))));
    setFocusedId(reveal.id);
    setReveal(null);
  }, [tree, reveal]);

  // Seçili düğümün kartı ve okumaları
  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!selectedId) {
      setDetail({ kind: 'idle' });
      return;
    }
    let live = true;
    setDetail((current) =>
      current.kind === 'ready' && current.detail.id === selectedId ? current : { kind: 'loading' },
    );
    source
      .equipment(selectedId)
      .then(async (equipment) => {
        const readings = equipment.counter
          ? await source.readings(equipment.counter.equipmentId)
          : [];
        if (live) setDetail({ kind: 'ready', detail: equipment, readings });
      })
      .catch((error: unknown) => {
        if (live) setDetail({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, selectedId, reloads]);

  const rows = useMemo(
    () => treeRows(nodes, expanded, treeQuery, collapsed, { criticalOnly }),
    [nodes, expanded, treeQuery, collapsed, criticalOnly],
  );
  const options = useMemo(() => equipmentOptions(nodes), [nodes]);
  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null;
  const particulars = data.kind === 'ready' ? data.particulars : null;
  // Gemi saati kaydından (ADR 0065 §4); yoksa dilim
  const timeZone = particulars ? vesselTime(particulars) : vesselTime(vessels?.[0] ?? null);
  const today = utcToShipLocal(now(), timeZone).slice(0, 10);
  const can = tree?.can ?? { create: false, edit: false, decommission: false, readings: false };
  const editor = source.editor;
  const editedOnBoard = editedOnBoardOf(source.scope, tree);
  const current = detail.kind === 'ready' ? detail.detail : null;
  const work = workData && workData.vesselId === vesselId ? workData : null;
  const subtree = useMemo(
    () => (selectedId ? descendantSet(nodes, selectedId) : new Set<string>()),
    [nodes, selectedId],
  );
  const plans = useMemo(
    () => (work?.plan?.plans ?? []).filter((p) => subtree.has(p.equipmentId)),
    [work, subtree],
  );
  const rowsOfSelection: OverviewRow[] = useMemo(
    () =>
      selectedId && work?.jobs
        ? overviewRows(nodes, selectedId, work.jobs.items, work.plan?.plans ?? [])
        : [],
    [nodes, selectedId, work],
  );
  const recordsOfSelection = useMemo(
    () => (work?.history ?? []).filter((h) => subtree.has(h.equipmentId)),
    [work, subtree],
  );
  const ordersOfSelection = useMemo(
    () => (work?.workOrders?.items ?? []).filter((w) => subtree.has(w.equipmentId)),
    [work, subtree],
  );
  const pointsOfSelection = useMemo(
    () => (work?.points ?? []).filter((p) => p.deletedAt === null && subtree.has(p.equipmentId)),
    [work, subtree],
  );
  // Seçilenin ölçümleri (History sekmesi; M7c): nokta başına okunur
  const [measured, setMeasured] = useState<{ key: string; items: Measurement[] } | null>(null);
  const pointKey = pointsOfSelection.map((p) => p.id).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: noktalar `pointKey` ile izlenir
  useEffect(() => {
    const read = source.measurements;
    if (!vesselId || !read || pointKey === '') {
      setMeasured(null);
      return;
    }
    let live = true;
    Promise.all(
      pointsOfSelection.map((p) => read(vesselId, { pointId: p.id }).catch(() => [])),
    ).then((lists) => {
      if (live) setMeasured({ key: pointKey, items: lists.flat() });
    });
    return () => {
      live = false;
    };
  }, [source, vesselId, pointKey]);
  const measurementsOfSelection = measured?.key === pointKey ? measured.items : [];
  const outOfPms = useMemo(
    () =>
      new Map<string, OutOfPmsReason[]>(
        (work?.alerts?.outOfPms ?? []).map((o) => [o.equipmentId, o.reasons]),
      ),
    [work],
  );
  const outOfPmsIds = useMemo(() => new Set(outOfPms.keys()), [outOfPms]);
  const marks = useCallback(
    (month: string) =>
      calendarMarks({
        month,
        plans,
        workOrders: ordersOfSelection,
        history: recordsOfSelection,
      }),
    [plans, ordersOfSelection, recordsOfSelection],
  );
  const navigate = (path: string) => shell?.navigate(path);
  const shipWork = source.scope === 'ship' && Boolean(source.work);
  const overviewActions = (row: OverviewRow) => (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" aria-label={t('pms.work.plan.more', { code: row.job.code })}>
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEye size={15} />}
          onClick={() => navigate(`/pms/plan?job=${row.job.id}`)}
        >
          {t('pms.work.plan.view')}
        </Menu.Item>
        {shipWork ? (
          <>
            <Menu.Item
              leftSection={<IconClipboardPlus size={15} />}
              onClick={async () => {
                try {
                  const wo = await source.work?.openWorkOrder({ jobId: row.job.id });
                  if (wo) navigate(`/pms/work-orders?wo=${wo.id}`);
                } catch {
                  navigate(`/pms/work-orders?status=open`);
                }
              }}
            >
              {t('pms.equipment.quickLinks.createWorkOrder')}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCalendarClock size={15} />}
              disabled={row.plan?.postponementPending === true}
              onClick={() => navigate(`/pms/plan?postpone=${row.job.id}`)}
            >
              {t('pms.work.plan.postpone')}
            </Menu.Item>
          </>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );

  const select = (id: string) => {
    setFocusedId(id);
    // Seçilenin yolu açık kalır (üst karttaki aramadan gelince)
    const path = ancestorIds(nodes, id);
    setExpanded((open) => new Set([...open, ...path]));
    setCollapsed((closed) => new Set([...closed].filter((c) => !path.includes(c))));
    setQuery({ equipment: id });
    setTreeDrawer(false);
  };
  const setOpen = (id: string, open: boolean) => {
    const next = withOpen({ expanded, collapsed }, id, open);
    setExpanded(next.expanded);
    setCollapsed(next.collapsed);
  };
  const toggle = (id: string) => {
    const row = rows.find((r) => r.node.id === id);
    if (row) setOpen(id, !row.expanded);
  };
  const onKey = (key: string, id: string) => {
    const action = treeKey(key, rows, id);
    if (!action) return;
    if (action.toggle) setOpen(action.toggle.id, action.toggle.open);
    if (action.focus) {
      setFocusedId(action.focus);
      setFocusRequest((n) => n + 1);
    }
    if (action.select) select(action.select);
  };
  const onQuery = (value: string) => {
    setTreeQuery(value);
    setCollapsed(new Set());
  };

  const reload = (selectId?: string) => {
    if (selectId) setPending({ id: selectId, staleTree: tree });
    setReloads((n) => n + 1);
  };

  useEffect(() => {
    if (!pending || drawer !== null || counterOpen) return;
    setPending(null);
    if (pending.id) setReveal({ id: pending.id, staleTree: pending.staleTree });
    setQuery({ equipment: pending.id });
  }, [pending, drawer, counterOpen, setQuery]);

  const submit = async (values: EquipmentFormValues) => {
    if (!editor || !vesselId) return;
    const input = toEquipmentInput(values);
    const parentId = values.parentId === '' ? null : values.parentId;
    if (drawer === 'edit' && current) {
      await editor.update(current.id, {
        rowVersion: current.rowVersion,
        ...(parentId !== current.parentId ? { parentId } : {}),
        ...input,
      });
      setDrawer(null);
      reload(current.id);
    } else {
      const created = await editor.create(vesselId, { parentId, ...input });
      setDrawer(null);
      // Kurulumdaki etkin gemi: satır gemide yazılır, buluta sonra gelir (ADR 0142 §3)
      if (isQueuedChange(created)) reload();
      else reload(created.id);
    }
  };

  const decommission = async (reason: string) => {
    if (!editor || !current) return;
    await editor.decommission(current.id, current.rowVersion, reason);
    setDrawer(null);
    setPending({ id: current.parentId, staleTree: tree });
    setReloads((n) => n + 1);
  };

  const loadSkeleton = async () => {
    if (!editor?.loadDefaultStructure || !vesselId) return;
    setSkeletonError(null);
    try {
      await editor.loadDefaultStructure(vesselId);
      initialised.current = null;
      reload();
    } catch (error) {
      setSkeletonError(error);
    }
  };

  // Ofiste PMS'i kurulmamış gemi: uyarı ve kütüphane sihirbazına düğme (04.11 notları P1).
  // Gemide kütüphane yok; orada "Load default structure" yolu kalır
  const setupPath = source.scope === 'web' && vesselId ? libraryApplyPath(vesselId) : null;
  const treeEmpty = (
    <>
      {setupPath ? (
        <LibrarySetupEmpty
          vesselName={vessels?.find((vessel) => vessel.id === vesselId)?.name ?? null}
          href={setupPath}
          onSetup={(event) => {
            if (shell) followLink(event, setupPath, shell.navigate);
          }}
        />
      ) : null}
      <EmptyState
        title={t('pms.equipment.tree.empty')}
        description={
          <>
            {source.scope === 'ship' || tree?.writer === 'cloud'
              ? can.create
                ? t(
                    source.scope === 'ship'
                      ? 'pms.equipment.tree.emptyShip'
                      : 'pms.equipment.tree.emptyCloud',
                  )
                : null
              : tree?.setup?.mode === 'queued' && can.create
                ? t('pms.equipment.tree.emptyQueued')
                : t('pms.equipment.tree.emptyWeb')}
            {skeletonError ? (
              <span className={classes.inlineError} role="alert">
                {errorText(skeletonError)}
              </span>
            ) : null}
          </>
        }
        action={
          can.create ? (
            <span className={classes.emptyActions}>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setDrawer('add')}
              >
                {t('pms.equipment.card.add')}
              </Button>
              {editor?.loadDefaultStructure ? (
                <Button size="xs" variant="default" onClick={() => void loadSkeleton()}>
                  {t('pms.equipment.tree.loadDefault')}
                </Button>
              ) : null}
            </span>
          ) : undefined
        }
      />
    </>
  );

  const treeView = (
    <EquipmentTree
      rows={rows}
      selectedId={selectedId}
      focusedId={focusedId}
      query={treeQuery}
      onQuery={onQuery}
      onSelect={select}
      onToggle={toggle}
      onKey={onKey}
      onFocus={(id) => setFocusedId(id)}
      empty={treeEmpty}
      focusRequest={focusRequest}
      criticalOnly={criticalOnly}
      onCriticalOnly={(value) => {
        setCriticalOnly(value);
        setCollapsed(new Set());
      }}
      outOfPms={outOfPmsIds}
    />
  );

  const failed = vesselsError ?? (data.kind === 'error' ? data.code : null);
  const loading = !failed && (vessels === null || (vesselId !== null && data.kind === 'loading'));
  const title = t('menu.pmsEquipment');

  if (vessels !== null && vessels.length === 0) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <Panel>
          <EmptyState title={t('pms.equipment.noVessel')} />
        </Panel>
      </div>
    );
  }

  return (
    <div className={classes.screen} data-pms-screen>
      <PageHeader
        title={title}
        titleHidden
        {...(selectedNode ? { crumb: selectedNode.name } : {})}
      />
      {libraryRequests ? (
        <LibraryRequestsBanner
          source={libraryRequests}
          onApplied={() => setReloads((n) => n + 1)}
        />
      ) : null}
      {failed ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={t('pms.equipment.error')}
            description={errorText({ code: failed })}
            action={
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => {
                  setVesselsError(null);
                  setData({ kind: 'loading' });
                  if (vessels === null) {
                    source
                      .vessels()
                      .then(setVessels)
                      .catch((error: unknown) => setVesselsError(errorCode(error)));
                  } else setReloads((n) => n + 1);
                }}
              >
                {t('pms.equipment.retry')}
              </Button>
            }
          />
        </Panel>
      ) : (
        <>
          <VesselPmsCard
            particulars={particulars}
            vessels={vessels ?? []}
            scope={source.scope}
            options={options}
            canAdd={Boolean(editor) && can.create}
            editedOnBoard={editedOnBoard}
            onVessel={(id) => {
              initialised.current = null;
              setQuery({ vessel: id });
            }}
            onPick={select}
            onAdd={() => setDrawer('add')}
            onOpenTree={() => setTreeDrawer(true)}
            // Export (M7e): seçili düğümün alt ağacı, seçim yoksa bütün gemi [NR496 4.4]
            {...(exporter.available
              ? {
                  onExport: (format: 'xlsx' | 'csv') =>
                    exporter.run(
                      'equipment-list',
                      format,
                      query.equipment ? { equipment: query.equipment } : {},
                    ),
                  exporting: exporter.busy,
                }
              : {})}
          />
          <SetupChangesBanner
            setup={tree?.setup}
            listPath={source.setupChanges && vesselId ? setupChangesPath(vesselId) : undefined}
          />
          {exporter.failed ? (
            <Alert
              color="red"
              withCloseButton
              closeButtonLabel={t('pms.work.detail.dismiss')}
              onClose={exporter.dismiss}
              role="alert"
            >
              {t('pmsReports.export.exportFailed')}
            </Alert>
          ) : null}
          <div className={classes.grid} aria-busy={loading}>
            <div className={classes.treeArea}>
              <Panel title={t('pms.equipment.tree.title')} className={classes.treePanel}>
                {loading ? <TreeSkeleton /> : treeView}
              </Panel>
            </div>
            <div className={classes.overviewArea}>
              <EquipmentOverview
                name={selectedNode?.name ?? null}
                counts={
                  selectedId
                    ? work?.plan
                      ? overviewSummary(nodes, selectedId, plans, recordsOfSelection, today)
                      : overviewCounts(nodes, selectedId)
                    : { totalComponents: 0, due: 0, overdue: 0, completed: 0 }
                }
                rows={source.plan && !work ? null : rowsOfSelection}
                nodes={nodes}
                rowActions={overviewActions}
              />
            </div>
            <div className={classes.detailsArea}>
              <ComponentDetails
                detail={current}
                readings={detail.kind === 'ready' ? detail.readings : []}
                canEdit={Boolean(editor) && can.edit}
                canEnterHours={
                  source.scope === 'ship' &&
                  Boolean(source.counter) &&
                  can.readings &&
                  Boolean(current?.counter)
                }
                editedOnBoard={editedOnBoard}
                onEdit={() => setDrawer('edit')}
                onEnterHours={() => setCounterOpen(true)}
                jobs={rowsOfSelection}
                records={recordsOfSelection}
                instructions={work?.instructions ?? []}
                onOpenManual={(id) => navigate(`/compliance/manuals?doc=${id}`)}
                points={pointsOfSelection}
                measurements={measurementsOfSelection}
                outOfPms={selectedId ? (outOfPms.get(selectedId) ?? []) : []}
                rotables={
                  source.rotables && vesselId && current && current.deletedAt === null ? (
                    <RotablesPanel
                      source={source}
                      vesselId={vesselId}
                      equipmentId={current.id}
                      currentHours={current.counter?.cumulativeHours ?? null}
                      today={today}
                    />
                  ) : undefined
                }
                {...(source.auditTrail && selectedId
                  ? {
                      onOpenAudit: () =>
                        navigate(`/pms/history?tab=audit&table=equipment&row=${selectedId}`),
                    }
                  : {})}
                {...(editor ? { onOutOfPms: setOutOfPmsMode } : {})}
                status={
                  loading || detail.kind === 'loading' ? (
                    <DetailSkeleton />
                  ) : detail.kind === 'error' ? (
                    <EmptyState
                      tone="danger"
                      title={t('pms.equipment.error')}
                      description={errorText({ code: detail.code })}
                      action={
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => setReloads((n) => n + 1)}
                        >
                          {t('pms.equipment.retry')}
                        </Button>
                      }
                    />
                  ) : undefined
                }
              />
            </div>
            <div className={classes.midArea}>
              <MaintenancePlanPanel
                today={today}
                plans={source.plan && !work ? null : plans}
                sortable={false}
                onOpen={(plan) => navigate(`/pms/plan?job=${plan.jobId}`)}
              />
              <UpcomingTasksPanel today={today} plans={source.plan && !work ? null : plans} />
            </div>
            <div className={classes.bottomArea}>
              <WorkOrderPanel
                scope={source.scope}
                items={source.workOrders && !work ? null : ordersOfSelection}
                canCreate={work?.workOrders?.can?.create ?? source.scope === 'ship'}
                onOpen={(wo) => navigate(`/pms/work-orders?wo=${wo.id}`)}
                {...(shipWork
                  ? {
                      onNew: () => navigate('/pms/work-orders?new=1'),
                      onGenerate: () => navigate('/pms/work-orders?generate=1'),
                    }
                  : {})}
              />
              <PmsCalendar today={today} marks={marks} />
              <QuickLinks scope={source.scope} />
            </div>
          </div>
          {editor && can.create ? (
            <div className={classes.mobileBar}>
              <Button
                fullWidth
                leftSection={<IconPlus size={16} />}
                onClick={() => setDrawer('add')}
              >
                {t('pms.equipment.card.add')}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <Drawer
        opened={treeDrawer}
        onClose={() => setTreeDrawer(false)}
        position="left"
        size={320}
        title={
          <span className={classes.modalTitle}>
            <IconListTree size={18} aria-hidden />
            {t('pms.equipment.tree.title')}
          </span>
        }
        closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
      >
        {treeDrawer ? treeView : null}
      </Drawer>

      {editor && tree ? (
        <EquipmentDrawer
          opened={drawer !== null}
          detail={drawer === 'edit' ? current : null}
          parent={drawer === 'add' ? addParentFor(nodes, selectedId) : null}
          nodes={nodes}
          today={today}
          canDecommission={can.decommission}
          onClose={() => setDrawer(null)}
          onSubmit={submit}
          onDecommission={decommission}
        />
      ) : null}

      {editor && current ? (
        <OutOfPmsDialog
          opened={outOfPmsMode !== null}
          mode={outOfPmsMode ?? 'mark'}
          name={current.name}
          onClose={() => setOutOfPmsMode(null)}
          onConfirm={async (reason) => {
            await editor.update(current.id, {
              rowVersion: current.rowVersion,
              outOfPmsManual: reason !== null,
              outOfPmsReason: reason,
            });
            setOutOfPmsMode(null);
            setReloads((n) => n + 1);
          }}
        />
      ) : null}

      {source.counter && current?.counter ? (
        <CounterDialog
          opened={counterOpen}
          counter={current.counter}
          timeZone={timeZone}
          editor={source.counter}
          now={now}
          onClose={() => setCounterOpen(false)}
          onSaved={() => {
            setCounterOpen(false);
            setReloads((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function TreeSkeleton() {
  return (
    <div className={classes.skeleton} aria-hidden>
      {Array.from({ length: 12 }, (_, i) => i).map((row) => (
        <span key={row} style={{ width: `${55 + ((row * 17) % 40)}%` }} />
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className={classes.skeleton} aria-hidden>
      {Array.from({ length: 8 }, (_, i) => i).map((row) => (
        <span key={row} style={{ width: `${70 + ((row * 13) % 30)}%` }} />
      ))}
    </div>
  );
}
